import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Nav from '@/components/Nav';
import {
  ArrowLeft, ArrowRight, Building2, Users, ShieldCheck, Bot, Heart,
  BarChart3, Car, Watch, Ship, Palette, Globe, Mail, CreditCard,
  Upload, FileSpreadsheet, Image, Layers, Home, Settings, Search,
  Star, Crown, Gem, Eye, MessageCircle, TrendingUp, Package,
  UserPlus, LogIn, ClipboardList, Sparkles, Map, BookOpen,
  DollarSign, PieChart, Target, Briefcase, ChevronRight, ChevronDown
} from 'lucide-react';

const WORKFLOW_DATA = {
  platform: {
    title: "ASTIIV BASGAT Platform",
    subtitle: "AI-Powered Ultra-Luxury Lifestyle & Real Estate Intelligence",
    stats: [
      { label: "User Roles", value: "4", icon: Users },
      { label: "Pages Built", value: "30+", icon: Globe },
      { label: "API Endpoints", value: "50+", icon: Layers },
      { label: "Integrations", value: "5", icon: Package },
    ]
  },
  userJourneys: [
    {
      id: "customer",
      title: "Customer Journey",
      icon: Crown,
      color: "gold",
      gradient: "from-amber-500/20 to-yellow-600/20",
      border: "border-gold/30",
      steps: [
        {
          title: "Landing Page",
          route: "/",
          icon: Globe,
          desc: "Premium landing with animated hero, feature showcase, testimonials, and subscription tiers",
          features: ["Smooth scrolling (Lenis)", "Animated sections", "CTA to Sign Up"]
        },
        {
          title: "Sign Up & Login",
          route: "/signup",
          icon: UserPlus,
          desc: "JWT-based authentication with email/password. Separate flows for customers vs partners",
          features: ["JWT tokens", "Role-based access", "Password hashing (bcrypt)"]
        },
        {
          title: "Lifestyle Onboarding",
          route: "/onboarding",
          icon: ClipboardList,
          desc: "Multi-step questionnaire capturing lifestyle preferences, budget, location interests, and dream lifestyle goals",
          features: ["Dream lifestyle quiz", "Budget preferences", "Location interests", "Property type preferences"]
        },
        {
          title: "Dashboard",
          route: "/dashboard",
          icon: Home,
          desc: "Personalized hub with Dream Match score, AI recommendations, lifestyle bundles, quick actions",
          features: ["Dream Match Score", "AI Recommendations", "Lifestyle Bundles", "Quick navigation cards"]
        },
        {
          title: "Properties",
          route: "/properties",
          icon: Building2,
          desc: "Browse luxury properties with advanced filters, sorting, virtual tours, and comparison tools",
          features: ["36+ property listings", "Filter by type/price/beds", "Sort options", "Property comparison"]
        },
        {
          title: "Property Detail",
          route: "/properties/:id",
          icon: Eye,
          desc: "Rich property pages with image galleries, 360° virtual tours, amenities, ROI calculator, and booking",
          features: ["Image gallery", "360° Virtual Tour (Pannellum)", "ROI Calculator", "Book Viewing form"]
        },
        {
          title: "Marketplace",
          route: "/marketplace",
          icon: Car,
          desc: "Luxury cars, watches, yachts, and lifestyle accessories with detailed listings",
          features: ["Cars, Watches, Yachts", "Category filtering", "Price ranges", "Detailed item pages"]
        },
        {
          title: "AI Concierge",
          route: "/concierge",
          icon: Bot,
          desc: "AI-powered lifestyle advisor using GPT-4o for personalized property and lifestyle recommendations",
          features: ["GPT-4o powered chat", "Context-aware advice", "Property suggestions", "Session-based conversations"]
        },
        {
          title: "Wishlist",
          route: "/wishlist",
          icon: Heart,
          desc: "Save properties, marketplace items, and lifestyle bundles. View bundle details in modal with all items",
          features: ["Save/remove items", "Bundle detail modal", "Rich item cards", "Quick navigation to details"]
        },
        {
          title: "Portfolio Preview",
          route: "/portfolio",
          icon: Briefcase,
          desc: "Curated lifestyle portfolio showing matched properties, vehicles, and accessories based on preferences",
          features: ["Dream Match integration", "Bundle creation", "Total value display", "Save to wishlist"]
        },
        {
          title: "Investor Portal",
          route: "/investor",
          icon: TrendingUp,
          desc: "Investment tools including market analysis, ROI projections, and portfolio tracking",
          features: ["Market overview", "Investment opportunities", "ROI tracking", "Portfolio insights link"]
        },
        {
          title: "Portfolio Insights",
          route: "/investor/insights",
          icon: PieChart,
          desc: "Premium/Elite dashboard with asset value growth, diversification charts, ROI projections, lifestyle score",
          features: ["Asset growth chart", "Diversification breakdown", "ROI projections", "Lifestyle score metrics"]
        },
        {
          title: "Subscription",
          route: "/subscription",
          icon: CreditCard,
          desc: "Tiered plans (Free, Premium AED 499/mo, Elite AED 999/mo) with Stripe integration",
          features: ["3 subscription tiers", "Stripe checkout", "Plan comparison", "Upgrade/downgrade"]
        },
        {
          title: "My Account",
          route: "/account",
          icon: Settings,
          desc: "View profile details, subscription status, onboarding preferences, and account settings",
          features: ["Profile info", "Subscription details", "Onboarding recap", "Edit preferences"]
        }
      ]
    },
    {
      id: "partner",
      title: "Partner Journey",
      icon: Briefcase,
      color: "blue",
      gradient: "from-blue-500/20 to-cyan-600/20",
      border: "border-blue-400/30",
      steps: [
        {
          title: "Partner Application",
          route: "/partner/apply",
          icon: ClipboardList,
          desc: "Industry-specific application forms for developers, brokers, car dealers, yacht dealers, designers",
          features: ["7 business types", "Document upload", "Trade license verification", "Auto-notification emails"]
        },
        {
          title: "Admin Review & Approval",
          route: "/admin",
          icon: ShieldCheck,
          desc: "Admin reviews applications, approves/rejects with branded email notifications to partners",
          features: ["Application queue", "Approve/Reject workflow", "Branded approval emails", "Account setup link"]
        },
        {
          title: "Account Setup",
          route: "/partner/setup",
          icon: Settings,
          desc: "Secure token-based setup where approved partners create their password and finalize account",
          features: ["Token verification", "Password creation", "Auto-role assignment", "Redirect to dashboard"]
        },
        {
          title: "Developer Dashboard",
          route: "/partner/dashboard/developer",
          icon: Building2,
          desc: "Full-featured portal with property management, analytics, CRM, and bulk upload capabilities",
          features: ["Project overview stats", "Property CRUD", "Bulk CSV/Excel upload", "Image & floor plan upload"]
        },
        {
          title: "Broker/Agent Dashboard",
          route: "/partner/dashboard/broker",
          icon: Users,
          desc: "Lead management, client tracking, property matching, and CRM tools for real estate agents",
          features: ["Lead management", "Client CRM", "Status tracking", "Performance metrics"]
        },
        {
          title: "Car Dealer Dashboard",
          route: "/partner/dashboard/cars",
          icon: Car,
          desc: "Inventory management with brand/model/color dropdowns, pricing, and lead tracking",
          features: ["Car inventory CRUD", "Brand/model selection", "Price management", "Lead tracking"]
        },
        {
          title: "Partner CRM",
          route: "/partner/dashboard/*",
          icon: Target,
          desc: "Shared CRM component across all partner types with leads, tasks, notes, and reminders",
          features: ["Lead pipeline", "Task management", "Notes & reminders", "Status filters"]
        },
        {
          title: "Bulk Property Upload",
          route: "/partner/dashboard/developer",
          icon: Upload,
          desc: "Upload 100-200+ projects at once via CSV/Excel with template download, validation, and media upload",
          features: ["CSV & Excel templates", "Row validation", "Image upload per project", "3D floor plan upload"]
        },
        {
          title: "Partner Account",
          route: "/partner/account",
          icon: Settings,
          desc: "View and edit partner profile, company details, and business information",
          features: ["Profile details", "Company info", "Business type", "Account status"]
        }
      ]
    },
    {
      id: "admin",
      title: "Admin Journey",
      icon: ShieldCheck,
      color: "red",
      gradient: "from-red-500/20 to-rose-600/20",
      border: "border-red-400/30",
      steps: [
        {
          title: "Admin Panel",
          route: "/admin",
          icon: ShieldCheck,
          desc: "Centralized management dashboard for the entire ASTIIV BASGAT platform",
          features: ["User management", "Partner approvals", "System metrics", "Content moderation"]
        },
        {
          title: "Partner Management",
          route: "/admin",
          icon: Users,
          desc: "Review, approve, or reject partner applications with automated email notifications",
          features: ["Application queue", "Approve/reject", "Branded emails (Resend)", "Partner status tracking"]
        },
        {
          title: "User Management",
          route: "/admin",
          icon: Crown,
          desc: "View all registered users, manage roles, subscription tiers, and account statuses",
          features: ["User list", "Role management", "Tier overview", "Account actions"]
        }
      ]
    }
  ],
  techStack: [
    { name: "React", desc: "Frontend framework with hooks, context, and React Router" },
    { name: "FastAPI", desc: "High-performance Python backend with async support" },
    { name: "MongoDB", desc: "NoSQL database with Motor async driver" },
    { name: "Tailwind CSS", desc: "Utility-first styling with custom luxury theme" },
    { name: "Shadcn/UI", desc: "Premium UI component library" },
    { name: "Framer Motion", desc: "Smooth animations and page transitions" },
    { name: "OpenAI GPT-4o", desc: "AI Concierge for lifestyle recommendations" },
    { name: "Stripe", desc: "Payment processing for subscriptions" },
    { name: "Resend", desc: "Transactional email service with branded templates" },
    { name: "Pannellum", desc: "360-degree virtual property tours" },
    { name: "Lenis", desc: "Ultra-smooth scrolling experience" },
    { name: "JWT", desc: "Secure token-based authentication" },
  ],
  emailSystem: [
    "Welcome Email (new signups)",
    "Password Reset",
    "Partner Application Received",
    "Partner Approved (with setup link)",
    "Partner Rejected",
    "Viewing Booking Confirmation",
    "Inquiry Received",
    "Subscription Confirmation",
  ]
};

const WorkflowPage = () => {
  const navigate = useNavigate();
  const [activeJourney, setActiveJourney] = useState("customer");
  const [expandedStep, setExpandedStep] = useState(null);

  const currentJourney = WORKFLOW_DATA.userJourneys.find(j => j.id === activeJourney);

  return (
    <div className="min-h-screen bg-obsidian text-text-primary">
      {/* Nav */}
      <Nav variant="public" showBack />

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">

        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 border border-gold/20 mb-6">
            <Sparkles className="w-4 h-4 text-gold" />
            <span className="text-gold text-sm font-medium">Complete Platform Overview</span>
          </div>
          <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-semibold mb-4">
            {WORKFLOW_DATA.platform.title}
          </h1>
          <p className="text-text-secondary text-lg max-w-2xl mx-auto">
            {WORKFLOW_DATA.platform.subtitle}
          </p>
        </motion.div>

        {/* Platform Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {WORKFLOW_DATA.platform.stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="glass-card p-5 text-center group hover:border-gold/30 transition-all"
            >
              <stat.icon className="w-8 h-8 text-gold mx-auto mb-2 group-hover:scale-110 transition-transform" />
              <div className="text-3xl font-bold">{stat.value}</div>
              <div className="text-text-secondary text-sm">{stat.label}</div>
            </motion.div>
          ))}
        </div>

        {/* Journey Selector Tabs */}
        <div className="flex flex-wrap gap-3 mb-8 justify-center" data-testid="journey-tabs">
          {WORKFLOW_DATA.userJourneys.map((journey) => (
            <button
              key={journey.id}
              onClick={() => { setActiveJourney(journey.id); setExpandedStep(null); }}
              data-testid={`journey-tab-${journey.id}`}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
                activeJourney === journey.id
                  ? `bg-gradient-to-r ${journey.gradient} ${journey.border} border shadow-lg`
                  : 'bg-white/5 border border-white/10 text-text-secondary hover:bg-white/10'
              }`}
            >
              <journey.icon className="w-5 h-5" />
              {journey.title}
              <span className="text-xs bg-white/10 px-2 py-0.5 rounded-full">{journey.steps.length}</span>
            </button>
          ))}
        </div>

        {/* Journey Flow */}
        <AnimatePresence mode="wait">
          {currentJourney && (
            <motion.div
              key={currentJourney.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <div className="relative">
                {/* Vertical Timeline Line */}
                <div className="absolute left-6 sm:left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-gold/50 via-gold/20 to-transparent hidden md:block" />

                <div className="space-y-4">
                  {currentJourney.steps.map((step, index) => (
                    <motion.div
                      key={step.title || `step-${index}`}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <div
                        className={`glass-card p-5 sm:p-6 ml-0 md:ml-16 cursor-pointer group transition-all hover:border-gold/20 ${
                          expandedStep === index ? `${currentJourney.border} border` : ''
                        }`}
                        onClick={() => setExpandedStep(expandedStep === index ? null : index)}
                        data-testid={`step-${currentJourney.id}-${index}`}
                      >
                        {/* Step Number Indicator (desktop) */}
                        <div className="absolute -left-2 hidden md:flex items-center justify-center w-12 h-12 rounded-full bg-obsidian border-2 border-gold/40 text-gold font-bold text-sm" style={{ marginLeft: '-3.5rem' }}>
                          {index + 1}
                        </div>

                        <div className="flex items-start gap-4">
                          <div className={`w-12 h-12 rounded-xl flex-shrink-0 flex items-center justify-center ${
                            expandedStep === index ? 'bg-gold/20' : 'bg-white/5 group-hover:bg-gold/10'
                          } transition-colors`}>
                            <step.icon className={`w-6 h-6 ${expandedStep === index ? 'text-gold' : 'text-text-secondary group-hover:text-gold'} transition-colors`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="md:hidden text-xs text-gold font-bold">Step {index + 1}</span>
                                  <h3 className="font-semibold text-lg">{step.title}</h3>
                                </div>
                                <code className="text-xs text-gold/60 bg-gold/5 px-2 py-0.5 rounded">{step.route}</code>
                              </div>
                              <ChevronDown className={`w-5 h-5 text-text-secondary transition-transform ${expandedStep === index ? 'rotate-180' : ''}`} />
                            </div>
                            <p className="text-text-secondary text-sm mt-2">{step.desc}</p>

                            <AnimatePresence>
                              {expandedStep === index && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: 'auto' }}
                                  exit={{ opacity: 0, height: 0 }}
                                  className="overflow-hidden"
                                >
                                  <div className="mt-4 pt-4 border-t border-white/10">
                                    <h4 className="text-xs uppercase tracking-wider text-text-secondary mb-3">Key Features</h4>
                                    <div className="flex flex-wrap gap-2">
                                      {step.features.map((feat, fi) => (
                                        <span key={`feature-${fi}`} className="px-3 py-1.5 rounded-lg bg-white/5 text-sm border border-white/10">
                                          {feat}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tech Stack */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="mt-16">
          <h2 className="font-playfair text-3xl font-semibold text-center mb-8">Technology Stack</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {WORKFLOW_DATA.techStack.map((tech, i) => (
              <motion.div
                key={tech.name}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 + i * 0.05 }}
                className="glass-card p-4 text-center group hover:border-gold/20 transition-all"
              >
                <h4 className="font-semibold text-gold mb-1">{tech.name}</h4>
                <p className="text-text-secondary text-xs">{tech.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Email System */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="mt-12">
          <h2 className="font-playfair text-3xl font-semibold text-center mb-8">Email System (Resend)</h2>
          <div className="glass-card p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {WORKFLOW_DATA.emailSystem.map((email, i) => (
                <div key={`journey-${i}`} className="flex items-center gap-3 p-3 rounded-lg bg-white/5">
                  <Mail className="w-4 h-4 text-gold flex-shrink-0" />
                  <span className="text-sm">{email}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Architecture Summary */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="mt-12 mb-16">
          <h2 className="font-playfair text-3xl font-semibold text-center mb-8">System Architecture</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="glass-card p-6">
              <h3 className="font-semibold text-gold text-lg mb-4 flex items-center gap-2"><Globe className="w-5 h-5" /> Frontend</h3>
              <ul className="space-y-2 text-sm text-text-secondary">
                <li className="flex items-center gap-2"><ChevronRight className="w-3 h-3 text-gold" /> React 18 + React Router v6</li>
                <li className="flex items-center gap-2"><ChevronRight className="w-3 h-3 text-gold" /> Tailwind CSS + Custom Luxury Theme</li>
                <li className="flex items-center gap-2"><ChevronRight className="w-3 h-3 text-gold" /> Shadcn/UI Components</li>
                <li className="flex items-center gap-2"><ChevronRight className="w-3 h-3 text-gold" /> Framer Motion Animations</li>
                <li className="flex items-center gap-2"><ChevronRight className="w-3 h-3 text-gold" /> Context API (Auth, Theme)</li>
                <li className="flex items-center gap-2"><ChevronRight className="w-3 h-3 text-gold" /> 30+ Pages, 10+ Components</li>
              </ul>
            </div>
            <div className="glass-card p-6">
              <h3 className="font-semibold text-blue-400 text-lg mb-4 flex items-center gap-2"><Layers className="w-5 h-5" /> Backend</h3>
              <ul className="space-y-2 text-sm text-text-secondary">
                <li className="flex items-center gap-2"><ChevronRight className="w-3 h-3 text-blue-400" /> FastAPI (Python 3.11)</li>
                <li className="flex items-center gap-2"><ChevronRight className="w-3 h-3 text-blue-400" /> 8 Router Modules (50+ endpoints)</li>
                <li className="flex items-center gap-2"><ChevronRight className="w-3 h-3 text-blue-400" /> JWT + RBAC Authentication</li>
                <li className="flex items-center gap-2"><ChevronRight className="w-3 h-3 text-blue-400" /> Pydantic Data Validation</li>
                <li className="flex items-center gap-2"><ChevronRight className="w-3 h-3 text-blue-400" /> Async MongoDB (Motor)</li>
                <li className="flex items-center gap-2"><ChevronRight className="w-3 h-3 text-blue-400" /> File Upload & Media Management</li>
              </ul>
            </div>
            <div className="glass-card p-6">
              <h3 className="font-semibold text-emerald-400 text-lg mb-4 flex items-center gap-2"><Package className="w-5 h-5" /> Integrations</h3>
              <ul className="space-y-2 text-sm text-text-secondary">
                <li className="flex items-center gap-2"><ChevronRight className="w-3 h-3 text-emerald-400" /> OpenAI GPT-4o (AI Concierge)</li>
                <li className="flex items-center gap-2"><ChevronRight className="w-3 h-3 text-emerald-400" /> Stripe (Subscriptions)</li>
                <li className="flex items-center gap-2"><ChevronRight className="w-3 h-3 text-emerald-400" /> Resend (Branded Emails)</li>
                <li className="flex items-center gap-2"><ChevronRight className="w-3 h-3 text-emerald-400" /> Pannellum (360° Tours)</li>
                <li className="flex items-center gap-2"><ChevronRight className="w-3 h-3 text-emerald-400" /> MongoDB Atlas</li>
                <li className="flex items-center gap-2"><ChevronRight className="w-3 h-3 text-emerald-400" /> Lenis (Smooth Scroll)</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default WorkflowPage;
